import { baseApi } from "@/shared/api";
import { fileResponseHandler, type FileResponse } from "@/shared/utils";
import type { CashFlowTimeSeries } from "@/features/finance/types/api";
import type {
  PLReportResponse,
  BalanceSheetResponse,
  CashFlowResponse,
  EquityChangesResponse,
} from "../types/reports";

interface CashFlowTimeSeriesParams {
  start_date: string;
  end_date: string;
  interval: string;
  project_id?: number;
  status_filter?: string;
}

interface PLReportParams {
  start_date: string;
  end_date: string;
  project_id?: number;
  status_filter?: string;
}

interface BalanceSheetParams {
  as_of_date: string;
  period_start_date?: string;
  project_id?: number;
  status_filter?: string;
}

interface CashFlowParams {
  start_date: string;
  end_date: string;
  project_id?: number;
  status_filter?: string;
}

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPLReport: builder.query<PLReportResponse, PLReportParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.status_filter) searchParams.set("status_filter", params.status_filter);
        return {
          url: `/api/accounting/pl-report/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Reports" as const, id: "PL_REPORT" }],
    }),

    getBalanceSheet: builder.query<BalanceSheetResponse, BalanceSheetParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("as_of_date", params.as_of_date);
        if (params.period_start_date) {
          searchParams.set("period_start_date", params.period_start_date);
        }
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.status_filter) searchParams.set("status_filter", params.status_filter);
        return {
          url: `/api/accounting/balance-sheet/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Reports" as const, id: "BALANCE_SHEET" }],
    }),

    getCashFlow: builder.query<CashFlowResponse, CashFlowParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.status_filter) searchParams.set("status_filter", params.status_filter);
        return {
          url: `/api/accounting/cash-flow/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Reports" as const, id: "CASH_FLOW" }],
    }),

    getEquityChanges: builder.query<EquityChangesResponse, { year: number }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("year", String(params.year));
        return {
          url: `/api/accounting/equity-changes/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
        };
      },
      providesTags: [{ type: "Reports" as const, id: "EQUITY_CHANGES" }],
    }),

    downloadEquityChanges: builder.mutation<FileResponse, { year: number }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("year", String(params.year));
        return {
          url: `/api/accounting/export/equity-changes/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
          responseHandler: fileResponseHandler,
        };
      },
    }),

    downloadBalanceSheet: builder.mutation<FileResponse, string | undefined>({
      query: (asOfDate) => {
        const searchParams = new URLSearchParams();
        if (asOfDate) {
          searchParams.set("as_of_date", asOfDate);
        }
        return {
          url: `/api/accounting/export/balance-sheet/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
          responseHandler: fileResponseHandler,
        };
      },
    }),

    downloadPLReport: builder.mutation<FileResponse, { start_date: string; end_date: string }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        return {
          url: `/api/accounting/export/pl-report/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
          responseHandler: fileResponseHandler,
        };
      },
    }),

    downloadBalanceSheetPdf: builder.mutation<FileResponse, { as_of_date: string; period_start_date?: string }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("as_of_date", params.as_of_date);
        if (params.period_start_date) {
          searchParams.set("period_start_date", params.period_start_date);
        }
        return {
          url: `/api/accounting/export/pdf/balance-sheet/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
          responseHandler: fileResponseHandler,
        };
      },
    }),

    downloadPLReportPdf: builder.mutation<FileResponse, { start_date: string; end_date: string }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        return {
          url: `/api/accounting/export/pdf/pl-report/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include",
          responseHandler: fileResponseHandler,
        };
      },
    }),

    getCashFlowTimeSeries: builder.query<CashFlowTimeSeries, CashFlowTimeSeriesParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("start_date", params.start_date);
        searchParams.set("end_date", params.end_date);
        searchParams.set("interval", params.interval);
        if (params.project_id !== undefined) searchParams.set("project_id", String(params.project_id));
        if (params.status_filter) searchParams.set("status_filter", params.status_filter);
        return {
          url: `/api/accounting/cash-flow/time-series/?${searchParams.toString()}`,
          method: "GET",
          credentials: "include" as const,
        };
      },
      providesTags: [{ type: "Reports" as const, id: "CASH_FLOW_TS" }],
    }),
  }),
});

export const {
  useGetPLReportQuery,
  useGetBalanceSheetQuery,
  useGetCashFlowQuery,
  useGetEquityChangesQuery,
  useDownloadEquityChangesMutation,
  useDownloadBalanceSheetMutation,
  useDownloadPLReportMutation,
  useDownloadBalanceSheetPdfMutation,
  useDownloadPLReportPdfMutation,
  useGetCashFlowTimeSeriesQuery,
} = reportsApi;

